import React, { useState, useEffect, useRef } from 'react';

const Portfolio: React.FC = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const portfolioRef = useRef<HTMLDivElement>(null);
  const autoPlayTimeout = useRef<NodeJS.Timeout | undefined>(undefined);

  const portfolioItems = [
    {
      image: "/rms_portfolio.png",
      title: "Remote Monitoring System (RMS)",
      description: "At goTuna, we developed a Remote Monitoring System (RMS) for a solar irrigation project, enabling real-time tracking and efficient control of pumps. Our solution includes an intuitive dashboard and mobile apps, allowing users to monitor performance, receive automated alerts, and remotely control pumps. With IoT integration and predictive analytics, our RMS ensures optimized water usage, reduced downtime, and enhanced system efficiency."
    },
    {
      image: "/vfd_portfolio.jpg",
      title: "Variable Frequency Drive (VFD)",
      description: "At goTuna, we extend our expertise beyond software to the hardware segment, delivering fully connected Variable Frequency Drive (VFD) solutions powered by IoT. Our VFD systems enable precise motor control, energy efficiency, and remote monitoring through seamless internet connectivity. With real-time data insights, automated alerts, and cloud integration, we help businesses optimize performance and reduce operational costs."
    },
    {
      image: "/bms_portfolio.png",
      title: "Battery Management System (BMS)",
      description: "At goTuna, we empower companies to offer Battery-as-a-Service (BaaS) with our enterprise-grade Battery Management System (BMS). Our solution includes State of Charge (SOC) monitoring, cell balancing, data logging, early warning systems, and remote battery locking, ensuring optimal performance, safety, and longevity. With real-time analytics and IoT connectivity, businesses can efficiently manage battery fleets and maximize uptime."
    }
  ];

  const startAutoPlay = () => {
    autoPlayTimeout.current = setTimeout(() => {
      handleNext();
    }, 3000);
  };

  const stopAutoPlay = () => {
    if (autoPlayTimeout.current) {
      clearTimeout(autoPlayTimeout.current);
    }
  };

  useEffect(() => {
    startAutoPlay();
    return () => stopAutoPlay();
  }, [currentIndex]);

  const handlePrev = () => {
    if (isAnimating) return;
    setIsAnimating(true);
    stopAutoPlay();
    setCurrentIndex((prevIndex) => 
      prevIndex === 0 ? portfolioItems.length - 1 : prevIndex - 1
    );
    setTimeout(() => setIsAnimating(false), 500);
  };

  const handleNext = () => {
    if (isAnimating) return;
    setIsAnimating(true);
    stopAutoPlay();
    setCurrentIndex((prevIndex) => (prevIndex + 1) % portfolioItems.length);
    setTimeout(() => setIsAnimating(false), 500);
  };

  const getItemClass = (index: number) => {
    if (index === currentIndex) return 'portfolio-item active';
    if (index === (currentIndex - 1 + portfolioItems.length) % portfolioItems.length) return 'portfolio-item prev';
    if (index === (currentIndex + 1) % portfolioItems.length) return 'portfolio-item next';
    return 'portfolio-item';
  };

  return (
    <section id="portfolio">
      <div className="container">
        <h2>Our Portfolio</h2>
        <div className="portfolio-carousel" ref={portfolioRef}>
          {portfolioItems.map((item, index) => (
            <div 
              key={index}
              className={getItemClass(index)}
              onMouseEnter={stopAutoPlay}
              onMouseLeave={startAutoPlay}
            >
              <img src={item.image} alt={item.title} />
              <div className="portfolio-content">
                <h3>{item.title}</h3>
                <p>{item.description}</p>
              </div>
            </div>
          ))}
          <button className="carousel-arrow prev" onClick={handlePrev}>
            <span>❮</span>
          </button>
          <button className="carousel-arrow next" onClick={handleNext}>
            <span>❯</span>
          </button>
          <div className="carousel-indicators">
            {portfolioItems.map((_, index) => (
              <button
                key={index}
                className={`indicator ${index === currentIndex ? 'active' : ''}`}
                onClick={() => {
                  if (isAnimating) return;
                  setIsAnimating(true);
                  stopAutoPlay();
                  setCurrentIndex(index);
                  setTimeout(() => setIsAnimating(false), 500);
                }}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Portfolio; 